/**
 * Polish Cards Against Humanity ("Karty"). Shared types and the in-memory round
 * engine. The live table is one global room, matching the old Socket.IO process
 * on :3001 — no chips, no persisted ranking, scores last only while you're seated.
 */

export const KARTY = {
  MIN_PLAYERS: 3,
  HAND_SIZE: 5,
  ROUND_END_MS: 5_000,
} as const;

export type KartyPhase = "waiting" | "playing" | "judging" | "result";

export const KARTY_PHASE_LABEL: Record<KartyPhase, string> = {
  waiting: "Czekamy na graczy",
  playing: "Zgrywać karty",
  judging: "Czar wybiera",
  result: "Koniec rundy",
};

export interface KartyPublicPlayer {
  userId: number;
  name: string;
  score: number;
  isCzar: boolean;
  played: boolean;
}

export interface KartySubmission {
  userId: number;
  name: string;
  card: string;
}

export interface KartyRoundResult {
  winnerUserId: number;
  winnerName: string;
  card: string;
}

export interface KartyGameUpdate {
  phase: KartyPhase;
  prompt: string | null;
  czarUserId: number | null;
  czarName: string | null;
  players: KartyPublicPlayer[];
  playerCount: number;
  needed: number;
  result: KartyRoundResult | null;
  nextRoundAt: number | null;
}

export interface KartySession {
  userId: number;
  hand: string[];
  playedCard: string | null;
  isCzar: boolean;
  submissions: KartySubmission[];
}

export type KartyPlayError =
  | "not_in_game"
  | "not_playing"
  | "is_czar"
  | "already_played"
  | "card_not_in_hand";

export type KartyJudgeError =
  | "not_in_game"
  | "not_czar"
  | "not_judging"
  | "unknown_winner";

export const KARTY_PLAY_ERROR_LABEL: Record<KartyPlayError, string> = {
  not_in_game: "Nie jesteś przy stole.",
  not_playing: "Teraz nie można zagrywać kart.",
  is_czar: "Czar rundy nie zagrywa karty.",
  already_played: "Już zagrałeś kartę w tej rundzie.",
  card_not_in_hand: "Nie masz tej karty.",
};

export const KARTY_JUDGE_ERROR_LABEL: Record<KartyJudgeError, string> = {
  not_in_game: "Nie jesteś przy stole.",
  not_czar: "Tylko Czar rundy wybiera zwycięzcę.",
  not_judging: "Jeszcze nie czas wybierać.",
  unknown_winner: "Nie ma takiej karty.",
};

export interface KartyPlayCardRequest {
  card: string;
}

export interface KartyChooseWinnerRequest {
  userId: number;
}

export const EMPTY_KARTY_UPDATE: KartyGameUpdate = {
  phase: "waiting",
  prompt: null,
  czarUserId: null,
  czarName: null,
  players: [],
  playerCount: 0,
  needed: KARTY.MIN_PLAYERS,
  result: null,
  nextRoundAt: null,
};

export function parseKartyDeck(text: string): string[] {
  const lines: string[] = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (line) lines.push(line);
  }
  return lines;
}

export function pickKartyIndex(length: number, rng: () => number): number {
  if (length <= 0) {
    throw new Error("Karty pick from empty list");
  }
  return Math.min(length - 1, Math.max(0, Math.floor(rng() * length)));
}

export function pickKartyItem<T>(items: readonly T[], rng: () => number): T {
  const item = items[pickKartyIndex(items.length, rng)];
  if (item === undefined) {
    throw new Error("Karty pick from empty list");
  }
  return item;
}

export function dealKartyHand(white: readonly string[], rng: () => number): string[] {
  return Array.from({ length: KARTY.HAND_SIZE }, () => pickKartyItem(white, rng));
}

export function kartyPlayersNeeded(playerCount: number): number {
  return Math.max(0, KARTY.MIN_PLAYERS - playerCount);
}

export function kartyCanStart(playerCount: number): boolean {
  return playerCount >= KARTY.MIN_PLAYERS;
}

export interface KartyEngineOptions {
  black: readonly string[];
  white: readonly string[];
  rng?: () => number;
  now?: () => number;
}

export interface KartyJoinInfo {
  isNew: boolean;
}

export interface KartyLeaveInfo {
  left: boolean;
  wasCzar: boolean;
}

export interface KartyEngine {
  join(userId: number, name: string): KartyJoinInfo;
  leave(userId: number): KartyLeaveInfo;
  playCard(userId: number, card: string): { ok: true } | { ok: false; error: KartyPlayError };
  chooseWinner(
    userId: number,
    winnerUserId: number,
  ): { ok: true; result: KartyRoundResult } | { ok: false; error: KartyJudgeError };
  nextRound(): boolean;
  publicState(): KartyGameUpdate;
  sessionFor(userId: number): KartySession | null;
  phase(): KartyPhase;
  playerIds(): number[];
}

interface Seat {
  userId: number;
  name: string;
  score: number;
  hand: string[];
  playedCard: string | null;
}

export function createKartyEngine(options: KartyEngineOptions): KartyEngine {
  const rng = options.rng ?? Math.random;
  const now = options.now ?? Date.now;
  const seats = new Map<number, Seat>();
  let phase: KartyPhase = "waiting";
  let prompt: string | null = null;
  let czarUserId: number | null = null;
  let result: KartyRoundResult | null = null;
  let nextRoundAt: number | null = null;
  const playOrder: number[] = [];

  function submissions(): KartySubmission[] {
    const list: KartySubmission[] = [];
    for (const userId of playOrder) {
      const seat = seats.get(userId);
      if (!seat || seat.playedCard === null) continue;
      list.push({ userId: seat.userId, name: seat.name, card: seat.playedCard });
    }
    return list;
  }

  function waitingState(): void {
    phase = "waiting";
    prompt = null;
    czarUserId = null;
    result = null;
    nextRoundAt = null;
    playOrder.length = 0;
    for (const seat of seats.values()) {
      seat.hand = [];
      seat.playedCard = null;
    }
  }

  function startRound(): boolean {
    if (!kartyCanStart(seats.size)) {
      waitingState();
      return false;
    }
    playOrder.length = 0;
    result = null;
    nextRoundAt = null;
    phase = "playing";
    prompt = pickKartyItem(options.black, rng);
    czarUserId = pickKartyItem([...seats.keys()], rng);
    for (const seat of seats.values()) {
      seat.playedCard = null;
      seat.hand = dealKartyHand(options.white, rng);
    }
    return true;
  }

  function maybeOpenJudging(): void {
    if (phase !== "playing" || czarUserId === null) return;
    const needed = seats.size - 1;
    if (needed <= 0) {
      startRound();
      return;
    }
    let played = 0;
    for (const seat of seats.values()) {
      if (seat.userId !== czarUserId && seat.playedCard !== null) played += 1;
    }
    if (played >= needed) {
      phase = "judging";
    }
  }

  function join(userId: number, name: string): KartyJoinInfo {
    const existing = seats.get(userId);
    if (existing) {
      existing.name = name;
      return { isNew: false };
    }
    const seat: Seat = {
      userId,
      name,
      score: 0,
      hand: phase === "playing" ? dealKartyHand(options.white, rng) : [],
      playedCard: null,
    };
    seats.set(userId, seat);
    if (phase === "waiting") {
      startRound();
    } else if (phase === "playing") {
      maybeOpenJudging();
    }
    return { isNew: true };
  }

  function leave(userId: number): KartyLeaveInfo {
    const seat = seats.get(userId);
    if (!seat) return { left: false, wasCzar: false };
    const wasCzar = czarUserId === userId;
    seats.delete(userId);
    const orderIndex = playOrder.indexOf(userId);
    if (orderIndex >= 0) playOrder.splice(orderIndex, 1);

    if (!kartyCanStart(seats.size)) {
      waitingState();
      return { left: true, wasCzar };
    }

    if (phase === "playing") {
      if (wasCzar) startRound();
      else maybeOpenJudging();
    } else if (phase === "judging" && wasCzar) {
      startRound();
    }

    return { left: true, wasCzar };
  }

  function playCard(
    userId: number,
    card: string,
  ): { ok: true } | { ok: false; error: KartyPlayError } {
    const seat = seats.get(userId);
    if (!seat) return { ok: false, error: "not_in_game" };
    if (phase !== "playing") return { ok: false, error: "not_playing" };
    if (seat.userId === czarUserId) return { ok: false, error: "is_czar" };
    if (seat.playedCard !== null) return { ok: false, error: "already_played" };
    if (!seat.hand.includes(card)) return { ok: false, error: "card_not_in_hand" };
    seat.playedCard = card;
    playOrder.push(userId);
    maybeOpenJudging();
    return { ok: true };
  }

  function chooseWinner(
    userId: number,
    winnerUserId: number,
  ): { ok: true; result: KartyRoundResult } | { ok: false; error: KartyJudgeError } {
    if (!seats.has(userId)) return { ok: false, error: "not_in_game" };
    if (userId !== czarUserId) return { ok: false, error: "not_czar" };
    if (phase !== "judging") return { ok: false, error: "not_judging" };
    const winner = seats.get(winnerUserId);
    if (!winner || winner.playedCard === null) return { ok: false, error: "unknown_winner" };
    winner.score += 1;
    const next: KartyRoundResult = {
      winnerUserId: winner.userId,
      winnerName: winner.name,
      card: winner.playedCard,
    };
    result = next;
    phase = "result";
    nextRoundAt = now() + KARTY.ROUND_END_MS;
    return { ok: true, result: next };
  }

  function nextRound(): boolean {
    nextRoundAt = null;
    result = null;
    return startRound();
  }

  function publicState(): KartyGameUpdate {
    const players: KartyPublicPlayer[] = [];
    for (const seat of seats.values()) {
      players.push({
        userId: seat.userId,
        name: seat.name,
        score: seat.score,
        isCzar: czarUserId === seat.userId,
        played: seat.playedCard !== null,
      });
    }
    const czar = czarUserId === null ? undefined : seats.get(czarUserId);
    return {
      phase,
      prompt,
      czarUserId,
      czarName: czar?.name ?? null,
      players,
      playerCount: seats.size,
      needed: kartyPlayersNeeded(seats.size),
      result,
      nextRoundAt,
    };
  }

  function sessionFor(userId: number): KartySession | null {
    const seat = seats.get(userId);
    if (!seat) return null;
    const isCzar = czarUserId === userId;
    return {
      userId,
      hand: [...seat.hand],
      playedCard: seat.playedCard,
      isCzar,
      submissions: phase === "judging" && isCzar ? submissions() : [],
    };
  }

  return {
    join,
    leave,
    playCard,
    chooseWinner,
    nextRound,
    publicState,
    sessionFor,
    phase: () => phase,
    playerIds: () => [...seats.keys()],
  };
}
